<template>
    <div class="modal-container-media-preview">
        <div v-if="enableInlineContainer && (!enableModal || !showModal)" class="inline-container">
            <MediaRender
                :content-type="contentType"
                :media-src="mediaSrc"
                :error-message="mediaError"
                :content-fit="
                    contentType.startsWith('audio') || contentType.startsWith('video') ? 'contain' : undefined
                "
            />
        </div>
        <portal to="modal" v-if="enableModal && showModal">
            <ModalContent
                darker-background
                overlay-brightness="dark"
                class="modal"
                width="full"
                height="full"
                :show-header="false"
                :show-close-button="true"
                :overflow-scroll="zoomed"
                content-padding="none"
                @close="onClose"
                :use-modal-overlay-defaults="false"
                close-button-variant="large"
            >
                <template #rightButtons>
                    <KButton
                        v-if="
                            !contentType.startsWith('audio') &&
                            !contentType.startsWith('video') &&
                            contentType !== 'application/pdf'
                        "
                        @click="toggleZoom"
                        size="icon"
                    >
                        <img v-if="zoomed" src="@/assets/minimize.svg" />
                        <img v-else src="@/assets/maximize.svg" />
                    </KButton>
                    <KButton :disabled="currentIndex === 0" @click="prevFile" size="icon">
                        <img src="@/assets/arrow_left.svg" />
                    </KButton>
                    <KButton :disabled="currentIndex >= files.length - 1" @click="nextFile" size="icon">
                        <img src="@/assets/arrow_right.svg" />
                    </KButton>
                </template>
                <MediaRender
                    :content-type="contentType"
                    :media-src="mediaSrc"
                    :error-message="mediaError"
                    :content-fit="getContentFit()"
                />
            </ModalContent>
        </portal>
    </div>
</template>

<script setup lang="ts">
    import ModalContent from "@/components/generic/ModalContent.vue";
    import { Portal } from "portal-vue";
    import { onMounted, ref, toRefs } from "vue";
    import { getConfig, klinikenApi } from "@/api";
    import MediaRender from "@/components/generic/preview/media/MediaRender.vue";
    import KButton from "@/components/Button.vue";
    import { useStore } from "@/store";

    const store = useStore();

    const emit = defineEmits(["close"]);

    const props = withDefaults(
        defineProps<{
            files?: Array<any>;
            currentIndex?: number;
            enableInlineContainer?: boolean;
            enableModal?: boolean;
            showModal?: boolean;
            fileMeta?: {
                patient: string;
                id: string;
            };
            file?: any;
            tabId?: Number;
            isGenerell?: boolean;
        }>(),
        {
            files: () => [],
            currentIndex: 0,
            enableInlineContainer: true,
            enableModal: true,
            showModal: false,
            isGenerell: false,
        }
    );

    const mediaSrc = ref<string>("");
    const contentType = ref<string>("");
    const zoomed = ref<boolean>(false);
    const mediaError = ref<string>("");
    const { fileMeta, showModal, file, enableModal, enableInlineContainer, isGenerell } = toRefs(props);

    const currentIndex = ref(props.currentIndex);

    const prevFile = () => {
        if (currentIndex.value > 0) {
            currentIndex.value--;
            carouselRetrieveMedia();
        }
    };
    const nextFile = () => {
        if (currentIndex.value < props.files.length - 1) {
            currentIndex.value++;
            carouselRetrieveMedia();
        }
    };

    const getContentFit = () => {
        if (contentType.value === "application/pdf") {
            return "overflow-y";
        }

        if (zoomed.value) {
            return "overflow";
        }

        return "contain";
    };

    const onClose = () => {
        emit("close");
    };

    // Kontrollera om byte-sekvensen matchar en signatur vid angivet offset.
    const bytesMatch = (bytes: Uint8Array, signature: number[], offset = 0) => {
        if (bytes.length < signature.length + offset) {
            return false;
        }

        return signature.every((value, index) => bytes[index + offset] === value);
    };

    // Försök att avgöra MIME-typ genom att läsa filens magiska bytes.
    const sniffMimeType = async (blob: Blob): Promise<string | null> => {
        const buffer = await blob.slice(0, 16).arrayBuffer();
        const bytes = new Uint8Array(buffer);

        if (bytesMatch(bytes, [0xff, 0xd8, 0xff])) {
            return "image/jpeg";
        }

        if (bytesMatch(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
            return "image/png";
        }

        if (bytesMatch(bytes, [0x47, 0x49, 0x46, 0x38])) {
            return "image/gif";
        }

        if (bytesMatch(bytes, [0x52, 0x49, 0x46, 0x46]) && bytesMatch(bytes, [0x57, 0x45, 0x42, 0x50], 8)) {
            return "image/webp";
        }

        if (bytesMatch(bytes, [0x25, 0x50, 0x44, 0x46])) {
            return "application/pdf";
        }

        if (bytesMatch(bytes, [0x66, 0x74, 0x79, 0x70], 4)) {
            return "video/mp4";
        }

        return null;
    };

    const resolveContentType = (sniffedType: string | null, blobType?: string) => {
        if (sniffedType) {
            return sniffedType;
        }

        if (blobType) {
            return blobType;
        }

        return "";
    };

    const carouselRetrieveMedia = async () => {
        await store.dispatch("tabs/displaySpinner", { id: props.tabId, display: true });
        mediaError.value = "";

        const currentFile = props.files && props.files.length > 0 ? props.files[currentIndex.value] : null;
        try {
            if (currentFile) {
                const url = isGenerell?.value
                    ? `filhantering/generellafiler/${currentFile.id}/file/`
                    : `filhantering/${currentFile.patient}/sendfile/${currentFile.id}`;

                const response = await klinikenApi.get(url, getConfig({ responseType: "blob" }, "Filer"));
                const blob = response.data as Blob;
                const sniffedType = await sniffMimeType(blob);

                contentType.value = resolveContentType(sniffedType, blob.type);
                mediaSrc.value = URL.createObjectURL(blob);
            }
        } catch (error) {
            mediaError.value = "Filen kan inte spelas upp. Den kan vara skadad.";
            contentType.value = "";
            mediaSrc.value = "";
        } finally {
            await store.dispatch("tabs/displaySpinner", { id: props.tabId, display: false });
        }
    };

    const retrieveMedia = async () => {
        await store.dispatch("tabs/displaySpinner", { id: props.tabId, display: true });
        mediaError.value = "";

        try {
            if (file?.value) {
                const blob = file.value as Blob;
                const sniffedType = await sniffMimeType(blob);

                contentType.value = resolveContentType(sniffedType, blob.type);
                mediaSrc.value = URL.createObjectURL(blob);
            } else if (fileMeta?.value?.id) {
                const url = isGenerell?.value
                    ? `filhantering/generellafiler/${fileMeta?.value?.id}/file/`
                    : `filhantering/${fileMeta?.value?.patient}/sendfile/${fileMeta.value.id}`;

                const response = await klinikenApi.get(url, getConfig({ responseType: "blob" }, "Filer"));
                const blob = response.data as Blob;
                const sniffedType = await sniffMimeType(blob);

                contentType.value = resolveContentType(sniffedType, blob.type);
                mediaSrc.value = URL.createObjectURL(blob);
            }
        } catch (error) {
            mediaError.value = "Filen kan inte spelas upp. Den kan vara skadad.";
            contentType.value = "";
            mediaSrc.value = "";
        } finally {
            await store.dispatch("tabs/displaySpinner", { id: props.tabId, display: false });
        }
    };

    const toggleZoom = () => {
        zoomed.value = !zoomed.value;
    };

    onMounted(() => {
        retrieveMedia();
    });
</script>

<style lang="scss" scoped>
    @import "@/style/variables";

    .modal-container-media-preview {
        .inline-container {
            overflow: auto;
            height: 100%;
            width: 100%;
        }
    }
</style>
